/* eslint-disable @typescript-eslint/no-unused-vars */

import React, { useEffect, useState } from 'react';
import { Formik, Form, Field } from 'formik';
import * as yup from 'yup';
import './RegisterForm.styles.sass';
import { Link, useLocation, useParams } from 'react-router-dom';
import PasswordStrengthChecker from '../../../components/common/PasswordStrengthChecker/PasswordStrengthChecker';
import { useTranslation } from 'react-i18next';
import {
    postPreCreateAccountThunkAction,
    postSignUpThunkAction,
    preCreateAccountThunkAction,
} from '../../../redux/auth/auth.slice';
import { useAppDispatch } from '../../../redux/hooks';

import { isPostSignUpSelector, preCreateAccountPayloadSelector } from '../../../redux/auth/auth.selectors';
import { useSelector } from 'react-redux';
import Spiner from '../../../components/common/Spiner/Spiner';
import { CEXModal } from '@careexpand/cex-core-components/esm';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPhone, faEnvelope, faEyeSlash, faEye } from '@fortawesome/pro-light-svg-icons';
import CexInputPhone from '../../../components/forms/CexInputPhone';
import moment from 'moment-timezone';
import { npiValid } from '../../../utils/npiValid';
import { normalizeTaxId, taxIdValid } from '../../../utils/taxIdValid';

const RegisterForm = () => {
    const [, setStatus] = useState<string>();
    const [, setIsSubmitting] = useState<boolean>();
    const signUpSelector = useSelector(isPostSignUpSelector);

    const [t]: any = useTranslation('global');
    const dispatch = useAppDispatch();
    const [registerError, setRegisterError] = useState<boolean>(false);
    const [passwordStrength, setPasswordStrength] = useState(0);
    const [passwordInput, setPasswordInput] = useState();
    const [errorMsg, setErrorMsg] = useState<any>();
    const [acceptanceChecked, setAcceptanceChecked] = useState<boolean>(false);
    const [isPasswordVisible, setIsPasswordVisible] = useState(false);
    const [isRepeatPasswordVisible, setIsRepeatPasswordVisible] = useState(false);
    const [passwordFieldError, setPasswordFieldError] = useState(false);
    const [repeatPasswordFieldError, setRepeatPasswordFieldError] = useState(false);
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const token = queryParams.get('token');

    const pregisterData: any = useSelector(preCreateAccountPayloadSelector);
    useEffect(() => {
        if (token) {
            dispatch(preCreateAccountThunkAction({ data: { token } }));
        }
    }, []);

    const closeModal = () => {
        setRegisterError(false);
    };

    const handleAcceptanceClick = () => {
        setAcceptanceChecked(!acceptanceChecked);
    };
    const checkPasswordStrength = (password: any) => {
        const strongPassword = new RegExp('(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[^A-Za-z0-9])(?=.{8,})');
        const mediumPassword = new RegExp(
            '((?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[^A-Za-z0-9])(?=.{6,}))|((?=.*[a-z])(?=.*[A-Z])(?=.*[^A-Za-z0-9])(?=.{8,}))'
        );
        if (password === '') setPasswordStrength(0);
        if (strongPassword.test(password)) {
            setPasswordStrength(3);
        } else if (mediumPassword.test(password)) {
            setPasswordStrength(2);
        } else {
            setPasswordStrength(1);
        }
    };
    const getCurrentTimezone = () => {
        return {
            zone: moment.tz.guess(),
            offset: moment().format('Z'),
        };
    };
    const handleOnSubmit = async (values: any) => {
        setIsSubmitting(false);
        setStatus('status');
        if (!token) {
            await dispatch(
                postSignUpThunkAction({
                    data: {
                        email: values.email,
                        firstName: values.name,
                        lastName: values.lastName,
                        phone: values.phone,
                        timezone: JSON.stringify(getCurrentTimezone()),
                        language: 'en',
                        password: values.password,
                        selectedTier: localStorage.getItem('tier') || '3',
                        npi: values.npi?.trim() || undefined,
                        taxId: normalizeTaxId(values.taxId) || undefined,
                        taxIdType: values.taxIdType === 'SSN' ? 'SSN' : 'EIN',
                    },
                })
            ).then((res: any) => {
                if (res.error) {
                    setRegisterError(true);
                    setErrorMsg(res.error.message.includes('401') ? 'Email already exists' : 'Error creating user');
                } else {
                    window.location.assign('/#/login?redirectFrom=register');
                }
            });
        } else {
            await dispatch(
                postPreCreateAccountThunkAction({
                    data: {
                        email: pregisterData.Email,
                        firstName: pregisterData.PName,
                        lastName: pregisterData.PLastname,
                        phone: pregisterData.Phone,
                        timezone: JSON.stringify(getCurrentTimezone()),
                        language: navigator.languages[0],
                        password: values.password,
                        token: token,
                    },
                })
            ).then((res: any) => {
                if (res.error) {
                    setRegisterError(true);
                    setErrorMsg(res.error.message.includes('401') ? 'Email already exists' : 'Error creating user');
                } else {
                    window.location.assign('/#/login?action=email');
                }
            });
        }
    };
    const validationSchema = yup.object().shape({
        name: token
            ? yup.string()
            : yup.string().required('Please, fill with your first name').min(2, 'Name should be at least 2 characters'),
        lastName: token
            ? yup.string()
            : yup
                  .string()
                  .required('Please, fill with your last name')
                  .min(2, 'Last Name should be at least 2 characters'),
        email: token
            ? yup.string()
            : yup.string().email('Please enter a valid email address').required('Please enter a valid email address'),
        password: yup
            .string()
            .min(8, 'Password must be at least 8 characters')
            .required('Please fill with your password'),
        repeatpassword: yup
            .string()
            .min(8, 'Password must be at least 8 characters')
            .required('Please confirm your password')
            .oneOf([yup.ref('password')], 'Passwords must match'),
        phone: token
            ? yup.string()
            : yup
                  .string()
                  .matches(/^\+[1-9]\d{1,14}$/, 'Please, fill with a valid Phone number')
                  .required('Please, fill with a valid Phone number'),
        npi: yup
            .string()
            .trim()
            .test('npi-valid', 'Please enter a valid 10-digit NPI number', value => {
                if (!value) return true;
                const digits = value.replace(/\D/g, '');
                return digits.length === 10 && npiValid(digits);
            }),
        taxId: yup
            .string()
            .trim()
            .test('tax-id-valid', 'Tax ID must be exactly 9 digits (EIN: XX-XXXXXXX, SSN: XXX-XX-XXXX)', value => {
                if (!value) return true;
                return taxIdValid(value);
            }),
    });

    return (
        <div className='userDetailsForm'>
            {registerError && (
                <CEXModal handleModal={() => closeModal()} title={'Register error'} subtitle={errorMsg} type={'info'} />
            )}
            {signUpSelector && <Spiner />}
            <Formik
                initialValues={{
                    email: pregisterData?.Email || '',
                    name: pregisterData?.PName || '',
                    lastName: pregisterData?.PLastname || '',
                    password: '',
                    repeatpassword: '',
                    phone: pregisterData?.Phone || '',
                    npi: pregisterData?.NPI ? String(pregisterData.NPI) : '',
                    taxId: '',
                    taxIdType: 'EIN',
                }}
                validationSchema={validationSchema}
                onSubmit={() => {
                    return;
                }}
            >
                {({
                    errors,
                    touched,
                    submitCount,
                    values,
                    validateForm,
                    handleChange,
                    handleBlur,
                    setFieldValue,
                }: any) => (
                    <Form className='userDetailsForm'>
                        <div className='inputFieldDivRegister'>
                            <div className='nameLastNameContainer'>
                                <div className='pi-input-skel-block'>
                                    <div className='pi-input'>
                                        <label className='pi-label' htmlFor=''>
                                            {t('register.first-name')}
                                        </label>
                                        <div className='pi-input-skel'>
                                            <span
                                                className='pi-input-prefix'
                                                style={{ padding: '10px', fontSize: '16px' }}
                                            >
                                                T
                                            </span>
                                            <Field
                                                name='name'
                                                type='text'
                                                value={pregisterData?.PName || values.name}
                                                className='pi-input-ele'
                                                placeholder={t('register.first-name')}
                                                disabled={token ? true : false}
                                            />
                                        </div>
                                    </div>
                                    <label className='input__error'>
                                        {errors.name && (touched.name || submitCount > 0) && errors.name}
                                    </label>
                                </div>
                                <div className='pi-input-skel-block'>
                                    <div className='pi-input'>
                                        <label className='pi-label' htmlFor=''>
                                            {t('register.last-name')}
                                        </label>
                                        <div className='pi-input-skel'>
                                            <span
                                                className='pi-input-prefix'
                                                style={{ padding: '10px', fontSize: '16px' }}
                                            >
                                                T
                                            </span>
                                            <Field
                                                name='lastName'
                                                type='text'
                                                value={pregisterData?.PLastname || values.lastName}
                                                className='pi-input-ele'
                                                placeholder={t('register.last-name')}
                                                disabled={token ? true : false}
                                            />
                                        </div>
                                    </div>
                                    <label className='input__error'>
                                        {errors.lastName && (touched.lastName || submitCount > 0) && errors.lastName}
                                    </label>
                                </div>
                            </div>
                        </div>

                        <div className='inputFieldDivRegister'>
                            <div className='nameLastNameContainer'>
                                <div className='pi-input-skel-block'>
                                    <div className='pi-input'>
                                        <label className='pi-label' htmlFor=''>
                                            {t('register.phone')}
                                        </label>
                                        <CexInputPhone
                                            value={
                                                pregisterData?.Phone && pregisterData.Phone.includes('+')
                                                    ? pregisterData.Phone
                                                    : pregisterData?.Phone
                                                    ? `+1${pregisterData.Phone.replace(/\D/g, '')}`
                                                    : values.phone || ''
                                            }
                                            onChange={(value: string) => {
                                                setFieldValue('phone', value);
                                            }}
                                            placeholder={t('register.phone-label')}
                                            readOnly={token ? true : false}
                                            error={
                                                errors.phone && (touched.phone || submitCount > 0)
                                                    ? errors.phone
                                                    : undefined
                                            }
                                            initialCountry='US'
                                            height='40px'
                                        />
                                    </div>
                                </div>
                                <div className='pi-input-skel-block'>
                                    <div className='pi-input'>
                                        <label className='pi-label' htmlFor=''>
                                            {t('register.email')}
                                        </label>
                                        <div className='pi-input-skel'>
                                            <span className='pi-input-prefix' style={{ padding: '8px 8px' }}>
                                                <FontAwesomeIcon
                                                    icon={faEnvelope}
                                                    size='2xl'
                                                    style={{ width: '17px' }}
                                                />
                                            </span>
                                            <Field
                                                name='email'
                                                type='email'
                                                disabled={token ? true : false}
                                                className='pi-input-ele'
                                                placeholder={t('register.email-label')}
                                                value={pregisterData?.Email || values.email}
                                            />
                                        </div>
                                    </div>
                                    <label className='input__error'>
                                        {errors.email && (touched.email || submitCount > 0) && errors.email}
                                    </label>
                                </div>
                            </div>
                        </div>
                        {!token && (
                            <>
                                <div className='inputFieldDivRegister'>
                                    <div className='nameLastNameContainer'>
                                        <div className='pi-input-skel-block'>
                                            <div className='pi-input'>
                                                <label className='pi-label' htmlFor=''>
                                                    NPI <span className='optional-label'>(optional)</span>
                                                </label>
                                                <div className='pi-input-skel'>
                                                    <span
                                                        className='pi-input-prefix'
                                                        style={{ padding: '10px', fontSize: '16px' }}
                                                    >
                                                        #
                                                    </span>
                                                    <Field
                                                        name='npi'
                                                        type='text'
                                                        value={values.npi}
                                                        className='pi-input-ele'
                                                        placeholder='National Provider Identifier'
                                                        maxLength={10}
                                                    />
                                                </div>
                                            </div>
                                            <label className='input__error'>
                                                {errors.npi && (touched.npi || submitCount > 0) && errors.npi}
                                            </label>
                                        </div>
                                    </div>
                                </div>
                                <div className='inputFieldDivRegister tax-id-full-row'>
                                    <div className='pi-input-skel-block'>
                                        <div className='pi-input'>
                                            <label className='pi-label' htmlFor=''>
                                                Tax ID <span className='optional-label'>(optional)</span>
                                            </label>
                                            <div className='pi-input-skel tax-id-row'>
                                                <Field as='select' name='taxIdType' className='tax-id-select'>
                                                    <option value='EIN'>EIN</option>
                                                    <option value='SSN'>SSN</option>
                                                </Field>
                                                <Field
                                                    name='taxId'
                                                    type='text'
                                                    value={values.taxId}
                                                    className='tax-id-input'
                                                    placeholder={
                                                        values.taxIdType === 'SSN' ? 'XXX-XX-XXXX' : 'XX-XXXXXXX'
                                                    }
                                                    maxLength={11}
                                                    inputMode='numeric'
                                                    autoComplete='off'
                                                />
                                            </div>
                                        </div>
                                        <label className='input__error'>
                                            {errors.taxId && (touched.taxId || submitCount > 0) && errors.taxId}
                                        </label>
                                    </div>
                                </div>
                            </>
                        )}
                        <div className='inputFieldDivRegister'>
                            <div className='nameLastNameContainer'>
                                <div className='pi-input-skel-block'>
                                    <div className='pi-input'>
                                        <label className='pi-label' htmlFor=''>
                                            {t('register.password')}
                                        </label>
                                        <div className='pi-input-skel'>
                                            <span
                                                className='pi-input-prefix'
                                                style={{ padding: '8px 8px', cursor: 'pointer' }}
                                                onClick={() => setIsPasswordVisible(!isPasswordVisible)}
                                            >
                                                <FontAwesomeIcon
                                                    icon={isPasswordVisible ? faEye : faEyeSlash}
                                                    size='2xl'
                                                    style={{ width: '17px' }}
                                                />
                                            </span>
                                            <Field
                                                name='password'
                                                type={isPasswordVisible ? 'text' : 'password'}
                                                placeholder={t('register.password-label')}
                                                className='pi-input-ele'
                                                validate={(e: any) => {
                                                    setPasswordInput(e);
                                                    checkPasswordStrength(passwordInput);
                                                    setPasswordFieldError(
                                                        errors.password && errors.password ? true : false
                                                    );
                                                }}
                                                onClick={() => {
                                                    setPasswordFieldError(false);
                                                }}
                                                onBlur={handleBlur}
                                            />
                                            <span className='tooltiptext'>
                                                You must include at least a capital and small letter, a number an a
                                                special character.
                                            </span>
                                        </div>
                                    </div>
                                    <label className='input__error'>
                                        {passwordFieldError &&
                                            errors.password &&
                                            (touched.password || submitCount > 0) &&
                                            errors.password}
                                    </label>
                                </div>
                                <div className='pi-input-skel-block'>
                                    <div className='pi-input'>
                                        <label className='pi-label' htmlFor=''>
                                            {t('register.repeat-password')}
                                        </label>
                                        <div className='pi-input-skel'>
                                            <span
                                                className='pi-input-prefix'
                                                style={{ padding: '8px 8px', cursor: 'pointer' }}
                                                onClick={() => setIsRepeatPasswordVisible(!isRepeatPasswordVisible)}
                                            >
                                                <FontAwesomeIcon
                                                    icon={isRepeatPasswordVisible ? faEye : faEyeSlash}
                                                    size='2xl'
                                                    style={{ width: '17px' }}
                                                />
                                            </span>
                                            <Field
                                                name='repeatpassword'
                                                type={isRepeatPasswordVisible ? 'text' : 'password'}
                                                placeholder={t('register.repeat-password-label')}
                                                className='pi-input-ele'
                                                onBlur={handleBlur}
                                                validate={() => {
                                                    setRepeatPasswordFieldError(
                                                        errors.repeatpassword && errors.repeatpassword ? true : false
                                                    );
                                                }}
                                            />
                                        </div>
                                    </div>
                                    <label className='input__error'>
                                        {repeatPasswordFieldError &&
                                            errors.repeatpassword &&
                                            (touched.repeatpassword || submitCount > 0) &&
                                            errors.repeatpassword}
                                    </label>
                                </div>
                            </div>
                        </div>
                        <PasswordStrengthChecker passwordStrength={passwordStrength} />
                        <div className='privacyPolicyContainer'>
                            <div className='commonText'>
                                <input
                                    onChange={() => {
                                        handleAcceptanceClick();
                                    }}
                                    required
                                    type='checkbox'
                                    checked={acceptanceChecked}
                                    className='registerFormCheckbox clickable'
                                />
                                <label>
                                    {t('register.accept-terms')}{' '}
                                    <a
                                        onClick={e => {
                                            e.preventDefault();
                                            window.location.href = '/#/terms';
                                        }}
                                        href='#'
                                        className='linkText'
                                    >
                                        {t('register.terms-policy')}
                                    </a>
                                </label>
                            </div>
                        </div>
                        <div className='reg_btn_container'>
                            <div className='backSignIn'>
                                <Link to='/login' className='backSignIn' style={{ textDecoration: 'none' }}>
                                    {t('register.login-link')}
                                </Link>
                            </div>
                            <div>
                                <button
                                    type='submit'
                                    className='Reg-blueButton'
                                    style={{
                                        fontSize:
                                            window.innerWidth <= 320
                                                ? '11px'
                                                : window.innerWidth === 768
                                                ? '10px'
                                                : window.innerWidth >= 426 && window.innerWidth <= 514
                                                ? '10px'
                                                : '14px',
                                    }}
                                    onClick={e => {
                                        e.preventDefault();
                                        validateForm();

                                        const selectedTier = localStorage.getItem('tier') || '3';
                                        if (!selectedTier)
                                            setErrorMsg('You must choose a tier before creating an account');
                                        if (
                                            acceptanceChecked &&
                                            errors &&
                                            Object.keys(errors).length === 0 &&
                                            selectedTier
                                        ) {
                                            handleOnSubmit(values);
                                        }
                                    }}
                                >
                                    {t('register.register-button')}
                                </button>
                            </div>
                        </div>
                    </Form>
                )}
            </Formik>
        </div>
    );
};

export default RegisterForm;
